import { Link } from "react-router";
import { listSharks } from "@/api/client";
import type { Benchmark, Constellation } from "@/api/types";
import { useAsync } from "@/hooks/useAsync";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { BenchmarkMetrics } from "@/site/benchmark";
import { grothTriangle } from "@/site/geometry";
import { SpecimenCard, SectionHead, count, to, useStats } from "@/site/parts";
import { Plate, plateCrop } from "@/site/plate";
import { ReferenceFigure, useReferenceShark } from "@/site/reference";
import { UploadStage } from "@/site/upload";

function More({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Link to={href} className="label link-u" style={{ color: "var(--ink)", whiteSpace: "nowrap" }}>
      {children} →
    </Link>
  );
}

function Spec() {
  const stats = useStats();
  const rows: [string, string][] = [
    ["Registry", `${count(stats?.sharks)} individuals`],
    ["Reference images", count(stats?.images)],
    ["Identifications", count(stats?.identifications)],
    ["Detector", "SAM 3.1 · body + spots"],
    ["Matcher", "Groth 1986 · triangle invariants"],
    ["Fused with", "MiewID · learned embedding"],
    ["Output", "Calibrated P(same individual)"],
  ];
  return (
    <dl className="kv">
      {rows.map(([k, v]) => (
        <div key={k}>
          <dt>{k}</dt>
          <dd>{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function RegistryStrip() {
  const state = useAsync("strip", (signal) => listSharks({ sort: "recent", limit: 12 }, signal));
  const stats = useStats();
  return (
    <section className="in-wrap in-section" aria-labelledby="registry-title" id="registry">
      <SectionHead index="01" title="Registry" id="registry-title">
        {count(stats?.sharks)} individuals, each a specimen record: catalogue number, rarity by
        sightings on record, and the constellation that identifies it. Most recently sighted first.{" "}
        <More href={to.registry}>Full registry</More>
      </SectionHead>
      {state.status === "error" && (
        <p className="alert" role="alert">
          The registry did not load: {state.error.message}
        </p>
      )}
      <div className="strip" aria-busy={state.status === "loading"}>
        {state.data?.items.map((s, i) => (
          <SpecimenCard key={s.id} shark={s} index={i} />
        ))}
      </div>
    </section>
  );
}

const STEPS = [
  {
    name: "Segment",
    text: "SAM 3.1 outlines the shark and finds every spot on the flank behind the gills.",
  },
  {
    name: "Extract",
    text: "Spot centres and sizes become a point set: the constellation, in pixel coordinates.",
  },
  {
    name: "Match",
    text: "Triangles of spots are compared by shape against every constellation in the registry.",
  },
  {
    name: "Calibrate",
    text: "The match score becomes a probability, set against a benchmark of known re-sightings.",
  },
];

/** Step four's figure: the shape of a calibration curve, drawn as a schematic, not data. */
function CalibrationSketch() {
  const points: string[] = [];
  for (let i = 0; i <= 40; i++) {
    const x = i / 40;
    const y = 1 / (1 + Math.exp(-14 * (x - 0.55)));
    points.push(`${40 + x * 540},${310 - y * 270}`);
  }
  return (
    <div className="plate plate-chart">
      <svg
        viewBox="0 0 600 360"
        role="img"
        aria-label="Calibration: score to probability, schematic"
      >
        <rect width="600" height="360" fill="var(--panel)" />
        <g stroke="var(--grat)">
          {[1, 2, 3, 4, 5, 6, 7].map((n) => (
            <line key={n} x1={n * 75} y1="0" x2={n * 75} y2="360" />
          ))}
          {[1, 2, 3, 4].map((n) => (
            <line key={n} x1="0" y1={n * 72} x2="600" y2={n * 72} />
          ))}
        </g>
        <line x1="40" y1="310" x2="580" y2="310" stroke="var(--ink-3)" strokeWidth="2" />
        <line x1="40" y1="310" x2="40" y2="30" stroke="var(--ink-3)" strokeWidth="2" />
        <polyline points={points.join(" ")} fill="none" stroke="var(--ink)" strokeWidth="5" />
        <line
          x1="40"
          y1="58"
          x2="580"
          y2="58"
          stroke="var(--signal)"
          strokeWidth="3"
          strokeDasharray="10 8"
        />
        <text
          x="572"
          y="44"
          textAnchor="end"
          fontFamily="var(--mono)"
          fontSize="26"
          fill="var(--signal-ink)"
        >
          threshold
        </text>
        <text
          x="572"
          y="296"
          textAnchor="end"
          fontFamily="var(--mono)"
          fontSize="24"
          fill="var(--ink-3)"
        >
          schematic
        </text>
      </svg>
    </div>
  );
}

function StepFigure({ step, c }: { step: number; c: Constellation | null | undefined }) {
  if (step === 3) return <CalibrationSketch />;
  if (!c) return <div className="plate plate-chart" style={{ aspectRatio: "5 / 3" }} />;
  const tri = step === 2 ? grothTriangle(c) : null;
  return (
    <Plate
      imageUrl={null}
      constellation={step === 0 ? { ...c, spots: [] } : c}
      crop={plateCrop(c, 5 / 3, 0.12)}
      aspect={5 / 3}
      outline={step === 0}
      triangle={tri?.vertices ?? null}
      compact
      label={`${STEPS[step]!.name}, drawn on the reference specimen`}
    />
  );
}

function Method({ c }: { c: Constellation | null | undefined }) {
  return (
    <section className="in-wrap in-section" id="method" aria-labelledby="method-title">
      <SectionHead index="02" title="Method" id="method-title">
        The whale shark photo-ID literature borrowed its matcher from astronomy: Groth&rsquo;s 1986
        algorithm for pairing star lists. Gillspotter reads spots as stars, and fuses them with a
        learned embedding where the geometry runs out. <More href={to.about}>The full method</More>
      </SectionHead>
      <ol className="steps" style={{ listStyle: "none", margin: 0, padding: 0 }}>
        {STEPS.map((s, i) => (
          <li key={s.name} className="step">
            <span className="label">
              {String(i + 1).padStart(2, "0")} · {s.name}
            </span>
            <StepFigure step={i} c={c} />
            <p style={{ fontSize: 15, maxWidth: "24em" }}>{s.text}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

function Reliability({ benchmark }: { benchmark: Benchmark | null | undefined }) {
  const metrics = benchmark?.metrics ?? [];
  if (metrics.length === 0) return null;
  return (
    <section className="in-wrap in-section" aria-labelledby="reliability-title">
      <SectionHead index="03" title="Reliability" id="reliability-title">
        Each labelled photograph is queried against all the others, through the same pipeline the
        site runs. <More href={`${to.about}#benchmark`}>Protocols and the full table</More>
      </SectionHead>
      <BenchmarkMetrics metrics={metrics.slice(0, 4)} />
      {benchmark?.updated_at && (
        <p className="label" style={{ marginTop: 16 }}>
          Benchmark updated {benchmark.updated_at}
        </p>
      )}
    </section>
  );
}

export function Home() {
  useDocumentMeta({});
  const reference = useReferenceShark();
  const stats = useStats();
  return (
    <>
      <section className="in-wrap in-grid hero" aria-labelledby="hero-title">
        <div className="hero-title">
          <p className="label" style={{ marginBottom: 20 }}>
            <span className="status-dot" /> Rhincodon typus · photo-identification
          </p>
          <h1 id="hero-title" className="display">
            Read the spots. Match the constellation. Know the shark.
          </h1>
        </div>
        <div className="hero-spec">
          <Spec />
        </div>
        <div id="identify" style={{ gridColumn: "span 7" }}>
          <UploadStage />
        </div>
        <div style={{ gridColumn: "8 / span 5" }}>
          <ReferenceFigure shark={reference.data} />
        </div>
      </section>
      <RegistryStrip />
      <Method c={reference.data?.constellation} />
      <Reliability benchmark={stats?.benchmark} />
    </>
  );
}
